import { useEffect, useRef, useState } from "react";
import { ApiError } from "../../shared/api/client";
import { TERMINAL_WORK_ORDER_SUGGESTION_MIN_LENGTH } from "../../../shared/production/terminal-work-orders";
import { productionGateway, type PlanTerminalWorkOrderSuggestion } from "./production-gateway";
import { OperatorWorkOrderRequestController } from "./operator-work-order-request-controller";

type Suggestions = { terminalCode: string; query: string; items: PlanTerminalWorkOrderSuggestion[]; nextCursor: string | null; phase: "waiting" | "ready"; warning: string | null };

export function useOperatorWorkOrderSuggestions(terminalCode: string, query: string, enabled: boolean) {
  const requests = useRef(new OperatorWorkOrderRequestController());
  const normalized = query.trim();
  const eligible = enabled && (normalized.length === 0 || normalized.length >= TERMINAL_WORK_ORDER_SUGGESTION_MIN_LENGTH);
  const [result, setResult] = useState<Suggestions>({ terminalCode: "", query: "", items: [], nextCursor: null, phase: "waiting", warning: null });
  useEffect(() => {
    const controller = requests.current;
    if (!eligible) { controller.cancelLookup(); return; }
    setResult({ terminalCode, query: normalized, items: [], nextCursor: null, phase: "waiting", warning: null });
    controller.scheduleLookup(async request => {
      try {
        const response = await productionGateway.terminalWorkOrderSuggestions(terminalCode, normalized, request.signal);
        if (request.isCurrent()) setResult({ terminalCode, query: normalized, items: response.items, nextCursor: response.nextCursor, phase: "ready", warning: response.warning });
      } catch (cause) {
        if (request.isCurrent()) setResult({ terminalCode, query: normalized, items: [], nextCursor: null, phase: "ready", warning: cause instanceof Error ? cause.message : "Hat planındaki iş emirleri alınamadı. Yeniden deneyin." });
      }
    });
    return () => controller.cancelLookup();
  }, [eligible, normalized, terminalCode]);
  const current = eligible && result.terminalCode === terminalCode && result.query === normalized;
  const loadMore = async () => {
    if (!current || !result.nextCursor || result.phase !== "ready") return;
    const request = requests.current.beginLookup();
    const cursor = result.nextCursor;
    setResult((previous) => ({ ...previous, phase: "waiting" }));
    try {
      const response = await productionGateway.terminalWorkOrderSuggestions(terminalCode, normalized, request.signal, cursor);
      if (!request.isCurrent()) return;
      setResult((previous) => previous.terminalCode === terminalCode && previous.query === normalized
        ? { ...previous, items: [...previous.items, ...response.items], nextCursor: response.nextCursor, phase: "ready", warning: response.warning }
        : previous);
    } catch (cause) {
      if (!request.isCurrent()) return;
      if (cause instanceof ApiError && cause.code === "TERMINAL_PLAN_CHANGED") {
        try {
          const response = await productionGateway.terminalWorkOrderSuggestions(terminalCode, normalized, request.signal);
          if (!request.isCurrent()) return;
          setResult((previous) => previous.terminalCode === terminalCode && previous.query === normalized
            ? { ...previous, items: response.items, nextCursor: response.nextCursor, phase: "ready", warning: response.warning ?? "Hat planı güncellendi; liste yenilendi." }
            : previous);
        } catch (refreshCause) {
          if (!request.isCurrent()) return;
          setResult((previous) => previous.terminalCode === terminalCode && previous.query === normalized
            ? { ...previous, items: [], nextCursor: null, phase: "ready", warning: refreshCause instanceof Error ? refreshCause.message : "Hat planı yenilenemedi. Yeniden deneyin." }
            : previous);
        }
        return;
      }
      setResult((previous) => previous.terminalCode === terminalCode && previous.query === normalized
        ? { ...previous, phase: "ready", warning: cause instanceof Error ? cause.message : "Hat planındaki iş emirleri alınamadı. Yeniden deneyin." }
        : previous);
    }
  };
  return {
    items: current ? result.items : [],
    loading: eligible && (!current || result.phase === "waiting"),
    ready: current && result.phase === "ready",
    warning: current ? result.warning : null,
    hasMore: current && result.nextCursor !== null,
    loadMore,
  };
}
